import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { signOut } from "aws-amplify/auth";
import { DeviceAPI, DeviceInfo } from "../../../services/device.service";
import { ApiError } from "../../../services/api.errors";
import { SettingsCard } from "../SettingsCard";

// ── Icons ─────────────────────────────────────────────────────────────────────

const MonitorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <rect x="5" y="2" width="14" height="20" rx="2" />
    <path d="M12 18h.01" />
  </svg>
);

const TabletIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <path d="M12 18h.01" />
  </svg>
);

const LaptopIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M20 16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9m16 0H4m16 0 1.28 2.55A1 1 0 0 1 20.37 20H3.63a1 1 0 0 1-.91-1.45L4 16" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────

function deviceIcon(os: string | null) {
  if (!os) return <MonitorIcon />;
  const o = os.toLowerCase();
  if (o === "iphone") return <PhoneIcon />;
  if (o === "ipad") return <TabletIcon />;
  if (o === "android") return <PhoneIcon />;
  if (o === "macos") return <LaptopIcon />;
  return <MonitorIcon />;
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 2) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ── Component ─────────────────────────────────────────────────────────────────

const ManageDevices: React.FC = () => {
  const [devices, setDevices] = useState<DeviceInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [signingOutAll, setSigningOutAll] = useState(false);
  const [confirmSignOutAll, setConfirmSignOutAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setDevices(await DeviceAPI.list());
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load devices.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startRename = (device: DeviceInfo) => {
    setRenaming(device.device_id);
    setRenameValue(device.name ?? "");
  };

  const saveRename = async (deviceId: string) => {
    const trimmed = renameValue.trim();
    if (!trimmed) { setRenaming(null); return; }
    setRenameSaving(true);
    try {
      const updated = await DeviceAPI.rename(deviceId, trimmed);
      setDevices(prev => prev.map(d => d.device_id === deviceId ? { ...d, name: updated.name } : d));
      setRenaming(null);
      toast.success("Device renamed.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Rename failed.");
    } finally {
      setRenameSaving(false);
    }
  };

  const removeDevice = async (deviceId: string) => {
    setRemoving(deviceId);
    try {
      await DeviceAPI.remove(deviceId);
      setDevices(prev => prev.filter(d => d.device_id !== deviceId));
      toast.success("Device signed out.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not remove device.");
    } finally {
      setRemoving(null);
    }
  };

  const handleSignOutAll = async () => {
    setSigningOutAll(true);
    try {
      await DeviceAPI.signOutAll();
      try { await signOut(); } catch { /* best-effort */ }
      window.location.href = "/login";
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Sign out failed.");
      setSigningOutAll(false);
      setConfirmSignOutAll(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* ── Trusted devices list ── */}
      <SettingsCard
        title="Trusted Devices"
        // Not "verified with an email OTP": clinic Admins who finished onboarding
        // skip the device gate, and every browser they use is trusted on first
        // request (Gate 3, get_current_user). Each fresh browser profile,
        // incognito window or automated browser appears here as its own row.
        subtitle="Browsers that have signed in to your account. Removing one erases its offline data key; accounts that use device verification must verify it again by email OTP."
        icon={<ShieldIcon />}
        headerRight={
          !loading && devices.length > 0 ? (
            <span className="text-xs text-gray-400">{devices.length} device{devices.length !== 1 ? "s" : ""}</span>
          ) : undefined
        }
      >
        {loading ? (
          <div className="py-6 text-center text-sm text-gray-400">Loading…</div>
        ) : devices.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-400">No trusted devices yet.</div>
        ) : (
          <div className="divide-y divide-gray-100 -mx-6 px-6">
            {devices.map((device, idx) => (
              <div
                key={device.device_id}
                className={`flex items-center gap-3 py-4 ${idx === devices.length - 1 ? "" : ""}`}
              >
                {/* Device type icon */}
                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 flex-shrink-0">
                  {deviceIcon(device.os)}
                </div>

                {/* Name + meta */}
                <div className="flex-1 min-w-0">
                  {renaming === device.device_id ? (
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") saveRename(device.device_id);
                          if (e.key === "Escape") setRenaming(null);
                        }}
                        className="flex-1 border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Device name"
                      />
                      <button
                        onClick={() => saveRename(device.device_id)}
                        disabled={renameSaving}
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-40"
                      >
                        {renameSaving ? "Saving…" : "Save"}
                      </button>
                      <button
                        onClick={() => setRenaming(null)}
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {device.name ?? device.platform ?? "Unknown device"}
                      </span>
                      {device.is_current && (
                        <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700 border border-green-200">
                          This device
                        </span>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">
                    {device.platform ?? "Unknown"} · Last seen {relativeTime(device.last_seen_at)}
                  </p>
                </div>

                {/* Actions */}
                {renaming !== device.device_id && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => startRename(device)}
                      title="Rename"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                      <PencilIcon />
                    </button>
                    {!device.is_current && (
                      <button
                        onClick={() => removeDevice(device.device_id)}
                        disabled={removing === device.device_id}
                        title="Sign out this device"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </SettingsCard>

      {/* ── Sign out all ── */}
      <SettingsCard
        title="Sign Out Everywhere"
        subtitle="Instantly revoke all active sessions and untrust all devices. Use this if a device is lost or compromised."
        icon={
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        }
      >
        {confirmSignOutAll ? (
          <div className="rounded-xl bg-red-50 border border-red-100 p-4 space-y-3">
            <p className="text-sm text-red-700 font-medium">Are you sure?</p>
            <p className="text-xs text-red-500">
              You'll be signed out on every device including this one. You can sign back in and re-verify your device.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleSignOutAll}
                disabled={signingOutAll}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40 transition-colors"
              >
                {signingOutAll ? "Signing out…" : "Yes, sign out everywhere"}
              </button>
              <button
                onClick={() => setConfirmSignOutAll(false)}
                disabled={signingOutAll}
                className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setConfirmSignOutAll(true)}
            className="w-full rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-left transition-colors hover:bg-red-100"
          >
            <span className="text-sm font-medium text-red-600">Sign out of all devices</span>
            <p className="text-xs text-red-400 mt-0.5">
              Logs you out everywhere immediately. Sessions are revoked within one request cycle.
            </p>
          </button>
        )}
      </SettingsCard>
    </div>
  );
};

export default ManageDevices;
